import { useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { modeloComRegistro, ROTULO_STATUS_REGISTRO, useModelosColeta, valorLegivel } from '../lib/coleta.js';
import { Aviso, Botao, Carregando, Cartao, Entrada, Modal, Selecao, Tabela, Vazio } from '../componentes/ui.jsx';
import { Agenda } from './Agenda.jsx';
import { DetalheRegistro, StatusRegistro, dataISOParaBR } from './registros/DetalheRegistro.jsx';
import './registros/Registros.css';

/**
 * Ordens de servico / fichas clinicas — a tela que ocupa o lugar da Agenda
 * quando o modelo de coleta da IA e OS ou ficha clinica.
 *
 * A agenda nao some: vira a segunda aba daqui. O registro e o "o que", o
 * horario e o "quando"; quando a IA (ou alguem) marca horario na conversa de
 * uma OS aberta, os dois ficam ligados.
 */

const FILTROS_STATUS = [
  ['abertas', 'Em aberto'],
  ['', 'Todas'],
  ...Object.entries(ROTULO_STATUS_REGISTRO)
];

const dataCurta = (ms) => new Date(ms).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' });

export function Registros() {
  const modelos = useModelosColeta();
  const [params, setParams] = useSearchParams();
  const aba = params.get('aba') === 'agenda' ? 'agenda' : 'lista';

  if (modelos.isLoading) return <Carregando />;
  if (modelos.isError) return <Aviso tom="perigo">{modelos.error.message}</Aviso>;

  const modelo = modeloComRegistro(modelos.data);
  // Modelo "Agendamento": nao ha OS nem ficha — a agenda e a tela.
  if (!modelo) return <Navigate to="/agenda" replace />;

  const irPara = (chave) => {
    const p = new URLSearchParams(params);
    if (chave === 'lista') p.delete('aba');
    else p.set('aba', chave);
    setParams(p, { replace: true });
  };

  return (
    <div className="coluna">
      <div className="abas reg-abas" role="tablist" aria-label={modelo.plural}>
        {[
          ['lista', `${modelo.icone} ${modelo.plural}`],
          ['agenda', '📅 Agenda']
        ].map(([chave, rotulo]) => (
          <button
            key={chave}
            type="button"
            role="tab"
            aria-selected={aba === chave}
            className={`abas__aba${aba === chave ? ' abas__aba--ativa' : ''}`}
            onClick={() => irPara(chave)}
          >
            {rotulo}
          </button>
        ))}
      </div>

      {aba === 'agenda' ? <Agenda /> : <ListaRegistros modelo={modelo} />}
    </div>
  );
}

function ListaRegistros({ modelo }) {
  const [status, setStatus] = useState('abertas');
  const [busca, setBusca] = useState('');
  const [aberto, setAberto] = useState(null);
  const [criando, setCriando] = useState(false);

  const lista = useQuery({
    queryKey: ['registros', 'lista', modelo.chave, status, busca],
    queryFn: () => api.get('/api/registros', { modelo: modelo.chave, ...(status ? { status } : {}), ...(busca.trim() ? { busca } : {}) }),
    placeholderData: (anterior) => anterior
  });

  const colunas = modelo.campos.filter((c) => c.naLista);
  const registros = lista.data?.registros ?? [];

  return (
    <>
      <header className="linha linha--entre">
        <div>
          <h1>{modelo.plural}</h1>
          <p className="texto-suave">
            A IA preenche conversando com o cliente; a equipe confere, completa e acompanha até concluir.
          </p>
        </div>
        <Botao onClick={() => setCriando(true)}>Nova {modelo.sigla ?? modelo.rotulo}</Botao>
      </header>

      <div className="linha reg-filtros">
        <Entrada placeholder="Buscar por cliente, telefone ou número..." value={busca} onChange={(e) => setBusca(e.target.value)} />
        <Selecao value={status} onChange={(e) => setStatus(e.target.value)}>
          {FILTROS_STATUS.map(([v, r]) => (
            <option key={v || 'todas'} value={v}>
              {r}
            </option>
          ))}
        </Selecao>
      </div>

      {lista.isError && <Aviso tom="perigo">{lista.error.message}</Aviso>}

      <Cartao semPadding>
        {lista.isLoading ? (
          <Carregando />
        ) : registros.length === 0 ? (
          <Vazio
            titulo={`Nenhuma ${modelo.rotulo.toLowerCase()} por aqui`}
            descricao="Quando a IA começar a coletar os dados de um cliente no WhatsApp, o registro aparece nesta lista."
          />
        ) : (
          <Tabela cabecalho={['Nº', 'Cliente', ...colunas.map((c) => c.rotulo), 'Dados', 'Horário', 'Status', 'Atualizada']}>
            {registros.map((r) => (
              <tr key={r.id} className="reg-linha" onClick={() => setAberto(r.id)} tabIndex={0} onKeyDown={(e) => e.key === 'Enter' && setAberto(r.id)}>
                <td>
                  <strong>{r.codigo}</strong>
                  {r.criadoPor === 'ia' && <span className="reg-ia" title="Aberta pela IA">IA</span>}
                </td>
                <td>
                  {r.leadNome}
                  <div className="texto-fraco">{r.leadTelefoneFormatado}</div>
                </td>
                {colunas.map((c) => (
                  <td key={c.chave} className="reg-celula-texto">
                    {valorLegivel(c, r.dados[c.chave]) || <span className="texto-fraco">—</span>}
                  </td>
                ))}
                <td>
                  <span className={r.progresso.completo ? 'reg-progresso reg-progresso--ok' : 'reg-progresso'}>
                    {r.progresso.preenchidosObrigatorios}/{r.progresso.obrigatorios}
                  </span>
                </td>
                <td>{r.agendamento ? `${dataISOParaBR(r.agendamento.data)} ${r.agendamento.hora}` : <span className="texto-fraco">—</span>}</td>
                <td>
                  <StatusRegistro valor={r.status} />
                </td>
                <td className="texto-suave">{dataCurta(r.updatedAt)}</td>
              </tr>
            ))}
          </Tabela>
        )}
      </Cartao>

      {aberto && <DetalheRegistro id={aberto} modelo={modelo} aoFechar={() => setAberto(null)} />}
      {criando && (
        <NovoRegistro
          modelo={modelo}
          aoFechar={() => setCriando(false)}
          aoCriar={(id) => {
            setCriando(false);
            setAberto(id);
          }}
        />
      )}
    </>
  );
}

/** Abrir uma OS / ficha na mao: escolhe o cliente e o painel abre para preencher. */
function NovoRegistro({ modelo, aoFechar, aoCriar }) {
  const queryClient = useQueryClient();
  const [busca, setBusca] = useState('');
  const contatos = useQuery({ queryKey: ['leads', 'busca-registro', busca], queryFn: () => api.get('/api/leads', { busca, limite: 8 }) });
  const criar = useMutation({
    mutationFn: (leadId) => api.post('/api/registros', { modelo: modelo.chave, leadId }),
    onSuccess: (r) => {
      queryClient.invalidateQueries({ queryKey: ['registros'] });
      aoCriar(r.registro.id);
    }
  });

  return (
    <Modal titulo={`Nova ${modelo.rotulo.toLowerCase()}`} aberto aoFechar={aoFechar} largura={600}>
      <div className="coluna">
        {criar.isError && <Aviso tom="perigo">{criar.error.message}</Aviso>}
        <h3 className="etapa__titulo">Para qual cliente?</h3>
        <Entrada autoFocus placeholder="Digite o nome ou telefone para buscar..." value={busca} onChange={(e) => setBusca(e.target.value)} />
        <div className="opcoes">
          {contatos.isLoading && <Carregando texto="Buscando..." />}
          {(contatos.data?.itens ?? []).map((c) => (
            <button key={c.id} type="button" className="opcao" disabled={criar.isPending} onClick={() => criar.mutate(c.id)}>
              <strong>{c.nome}</strong>
              <span>{c.telefoneFormatado}</span>
            </button>
          ))}
        </div>
        {contatos.data && contatos.data.itens.length === 0 && <p className="texto-fraco">Nenhum cliente encontrado com esse nome.</p>}
      </div>
    </Modal>
  );
}
